<template>
  <ValidationProvider
    :class="['form__control', className]"
    :vid="vid"
    :rules="rules"
    :name="name"
    :mode="validationMode"
    :custom-messages="customMessages"
    tag="div"
    ref="provider"
    v-slot="{ errors, validated, invalid }"
  >
    <template v-if="pslStyle === 'image'">
      <v-radio-group
        v-model="innerValue"
        row
        class="pa-0 ma-0 mt-n2 radio-image__wrapper"
        :hide-details="hideDetails"
        :id="vid"
        :data-uiid="dataUiid"
        :error-messages="errors[0] || externalErrorMsg"
        :aria-label="ariaLabel"
      >
        <v-btn
          v-for="(item, index) in items"
          :key="index"
          depressed
          :style="{ 'border-radius': imageStyleBorderRadius + 'px' }"
          outlined
          class="d-flex flex-column justify-center align-center mt-2 mr-2"
          :width="width ?? '160px'"
          :height="height ?? '144px'"
          :color="
            setColor(item, validated, invalid, {
              base: 'webgrey',
              selected: 'accent',
              error: 'error',
            })
          "
          :data-uiid="item.dataUiid"
          :class="
            setState(item, validated, invalid, {
              base: '',
              selected: 'radio__selected',
              error: 'radio__error',
            })
          "
          :disabled="disabled"
          @click="getValue(item.value)"
          :aria-label="cleanAriaLabel(item.text)"
        >
          <div class="d-flex flex-column justify-center align-center">
            <v-img
              :src="require(`@/assets/icons/${item.image.path}`)"
              contain
              :class="['mb-3', item.image.class]"
              :width="item.image.width"
              :height="item.image.height ? item.image.height : 'auto'"
              :alt="item.image.alt"
            ></v-img>
            <span
              class="ma-0 font-weight-bold text-center text-none"
              :class="[
                setTextColor(item, validated, invalid, {
                  base: 'black--text',
                  selected: 'black--text',
                  error: 'error--text',
                }),
                labelClass,
              ]"
              v-html="item.text"
            />
          </div>
        </v-btn>
      </v-radio-group>
    </template>

    <template v-if="pslStyle === 'button'">
      <v-radio-group
        v-model="innerValue"
        row
        class="pa-0 ma-0 mt-2 psl-base-radio--button"
        :hide-details="hideDetails"
        :id="vid"
        :error-messages="errors[0] || externalErrorMsg"
        :data-uiid="dataUiid"
        :class="[
          'input__control',
          { uppercase: isUppercase, 'field-invalid': invalid },
        ]"
        :aria-label="ariaLabel"
      >
        <v-btn
          v-for="(item, index) in items"
          :key="index"
          depressed
          rounded
          :width="
            width ?? ($vuetify.breakpoint.smAndDown ? 'calc(50% - 6px)' : '140')
          "
          :height="height ?? '48px'"
          :data-uiid="item.dataUiid"
          :outlined="item.value !== innerValue"
          :color="
            setColor(item, validated, invalid, {
              base: 'webgrey',
              selected: 'webgrey',
              error: 'error',
            })
          "
          :class="[
            setState(item, validated, invalid, {
              base: '',
              selected: 'radio__selected',
              error: 'radio__error',
            }),
          ]"
          :disabled="item?.disabled ?? disabled"
          @click="getValue(item.value)"
          :aria-label="cleanAriaLabel(item.text)"
          ><span
            class="font-20 font-weight-regular text-none"
            :class="
              setTextColor(item, validated, invalid, {
                base: 'radiobutton--text',
                selected: 'white--text font-weight-bold',
                error: 'error--text',
              })
            "
            >{{
              $vuetify.breakpoint.mdAndDown && item.mobileText
                ? item.mobileText
                : item.text
            }}</span
          ></v-btn
        >

        <template v-slot:message="{ message }">
          <span v-html="message"></span>
        </template>
      </v-radio-group>
    </template>

    <template v-if="pslStyle === 'radio'">
      <v-radio-group
        v-model="innerValue"
        row
        class="pa-0 ma-0 mt-n2"
        :disabled="disabled"
        :hide-details="hideDetails"
        :id="vid"
        :error-messages="errors[0] || externalErrorMsg"
        :data-uiid="dataUiid"
        :class="[
          'input__control',
          { uppercase: isUppercase, 'field-invalid': invalid },
        ]"
        :aria-label="ariaLabel"
      >
        <v-radio
          v-for="(item, index) in items"
          :key="index"
          :value="item.value"
          :data-uiid="item.dataUiid"
          color="psdarkgreen"
          class="mt-4 mr-8"
          :aria-label="cleanAriaLabel(item.text)"
        >
          <template v-slot:label>
            <span
              :class="
                setTextColor(item, validated, invalid, {
                  base: 'black--text',
                  selected: 'black--text',
                  error: 'error--text',
                })
              "
              >{{ item.text }}</span
            >
          </template>
        </v-radio>
      </v-radio-group>
    </template>

    <template v-if="pslStyle === 'card'">
      <v-radio-group
        v-model="innerValue"
        row
        class="radioButtonCard mt-0"
        :data-uiid="dataUiid"
        :hide-details="hideDetails"
        :aria-label="ariaLabel"
      >
        <v-row>
          <v-col cols="6" class="radioButtonCard__left">
            <v-card
              :ripple="false"
              class="elevation-0 px-3 px-lg-6 py-5"
              :class="{
                'radioButtonCard--active': innerValue,
              }"
            >
              <div class="d-flex flex-column">
                <div>
                  <h3 class="font-20 text-center mb-6 d-none d-lg-block">
                    Stay covered
                  </h3>
                  <h3 class="font-18 text-center my-5 d-block d-lg-none">
                    Stay<br />covered
                  </h3>
                  <ul class="mb-3 d-none d-lg-block">
                    <li
                      v-for="(renew, index) in cardConfigs.autoRenew"
                      class="d-flex"
                      :key="index"
                    >
                      <span class="mr-3"
                        ><v-icon>mdi-checkbox-marked-circle</v-icon></span
                      >
                      <div>
                        <p class="font-weight-bold ma-0 mb-1">
                          {{ renew.title }}
                        </p>
                        <p class="font-14">
                          {{ renew.description }}
                        </p>
                      </div>
                    </li>
                  </ul>
                </div>
                <v-btn
                  depressed
                  rounded
                  x-large
                  outlined
                  class="text-capitalize mt-auto"
                  :ripple="false"
                  data-uiid="AutoRenewalConsent_yes"
                  :height="$vuetify.breakpoint.lgAndUp ? '60' : '48'"
                  @click="getValue(true)"
                  aria-label="Stay covered"
                >
                  <span v-if="innerValue" class="d-inline-flex align-center">
                    <v-icon color="white" class="d-none d-lg-block mr-2"
                      >mdi-checkbox-marked-circle</v-icon
                    >
                    <span
                      class="font-weight-bold"
                      :class="setTextSize('font-20', 'font-16')"
                      >Selected</span
                    >
                  </span>
                  <span v-else :class="setTextSize('font-20', 'font-16')"
                    >Select</span
                  >
                </v-btn>
              </div>
            </v-card>
          </v-col>
          <v-col cols="6" class="radioButtonCard__right">
            <v-card
              :ripple="false"
              class="elevation-0 px-3 px-lg-6 py-5"
              :class="{
                'radioButtonCard--active': !innerValue,
              }"
            >
              <div class="d-flex flex-column">
                <div>
                  <h3 class="font-20 text-center mb-6 d-none d-lg-block">
                    Do not auto-renew my policy
                  </h3>
                  <h3 class="font-18 text-center my-5 d-block d-lg-none">
                    Don't auto<br />renew
                  </h3>
                  <ul class="mb-3 d-none d-lg-block">
                    <li
                      v-for="(
                        notAutoRenew, index
                      ) in cardConfigs.doNotAutoRenew"
                      class="d-flex"
                      :key="index"
                    >
                      <span class="mr-3"
                        ><v-icon>mdi-checkbox-marked-circle</v-icon></span
                      >
                      <div>
                        <p class="font-weight-bold ma-0 mb-1">
                          {{ notAutoRenew.title }}
                        </p>
                        <p class="font-14">
                          {{ notAutoRenew.description }}
                        </p>
                      </div>
                    </li>
                  </ul>
                </div>
                <v-btn
                  depressed
                  rounded
                  x-large
                  outlined
                  class="text-capitalize mt-auto"
                  :ripple="false"
                  data-uiid="AutoRenewalConsent_no"
                  :height="$vuetify.breakpoint.lgAndUp ? '60' : '48'"
                  @click="getValue(false)"
                  aria-label="Do not auto-renew my policy"
                >
                  <span v-if="!innerValue" class="d-inline-flex align-center">
                    <v-icon color="white" class="d-none d-lg-block mr-2"
                      >mdi-checkbox-marked-circle</v-icon
                    >
                    <span
                      class="font-weight-bold"
                      :class="setTextSize('font-20', 'font-16')"
                      >Selected</span
                    >
                  </span>
                  <span v-else :class="setTextSize('font-20', 'font-16')"
                    >Select</span
                  >
                </v-btn>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-radio-group>
    </template>
  </ValidationProvider>
</template>

<script>
import { ValidationProvider } from "vee-validate";
import {
  AUTO_RENEW,
  DO_NOT_AUTO_RENEW,
} from "@/constants/payment.constants.js";

export default {
  name: "BaseRadio",
  components: {
    ValidationProvider,
  },
  props: {
    vid: String,
    value: [String, Boolean],
    className: String,
    name: String,
    fieldName: String,
    label: String,
    labelClass: String,
    externalErrorMsg: String,
    customMessages: Object,
    placeholder: String,
    hideDetails: {
      type: [Boolean, String],
      default: "auto",
    },
    isUppercase: Boolean,
    autocomplete: String,
    mask: String,
    tooltip: Object,
    rules: [Object, String],
    validationMode: {
      default: "eager",
      required: false,
    },
    hideValidationIcon: Boolean,
    items: Array,
    dataUiid: String,
    pslStyle: {
      type: String,
      default: "radio",
    },
    width: String,
    height: String,
    imageStyleBorderRadius: {
      type: Number,
      default: 5,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    cardConfigs: {
      type: Object,
      default: () => {
        return {
          autoRenew: AUTO_RENEW,
          doNotAutoRenew: DO_NOT_AUTO_RENEW,
        };
      },
    },
    ariaLabel: String,
  },
  data() {
    return {
      innerValue: null,
    };
  },
  watch: {
    // Handles internal model changes.
    innerValue(value) {
      this.$emit("input", value, this.fieldName, this.$refs.provider);
    },
    // Handles external model changes.
    value(val) {
      if (val !== this.innerValue) {
        this.innerValue = val;
      }
    },
  },
  created() {
    if (this.value) {
      this.innerValue = this.value;
    }
  },
  mounted() {
    // trigger validate
    if (this.innerValue) {
      this.$refs.provider.validate();
    }
  },
  methods: {
    displayValidationIcon(errors, externalErrorMsg, validated) {
      if (this.hideValidationIcon) {
        return "";
      }
      return errors[0] || externalErrorMsg ? "" : validated ? "icon-check" : "";
    },
    getValue(value) {
      this.innerValue = value;
      this.$emit("click", this.innerValue);
    },
    setTextColor(item, validated, invalid, styles) {
      if (validated && invalid) {
        return styles.error;
      }
      if (item.value === this.innerValue) {
        return styles.selected;
      }
      return styles.base;
    },
    setColor(item, validated, invalid, styles) {
      if (validated && invalid) {
        return styles.error;
      }
      if (item.value === this.innerValue) {
        return styles.selected;
      }
      return styles.base;
    },
    setState(item, validated, invalid, styles) {
      if (validated && invalid) {
        return styles.error;
      }
      if (item.value === this.innerValue) {
        return styles.selected;
      }
      return styles.base;
    },
    setTextSize(desktop, mobile) {
      return this.$vuetify.breakpoint.lgAndUp ? desktop : mobile;
    },
    cleanAriaLabel(label) {
      if (!label) return "";
      return label
        .replaceAll("&amp;", "and")
        .replaceAll("<br>", " ")
        .replaceAll("<br />", " ");
    },
  },
};
</script>

<style lang="scss" scoped>
.radio__error {
  border-width: 2px;
}

.radio__selected {
  border-width: 4px;

  &:before {
    opacity: 0.08;
  }
}

.v-btn:disabled {
  opacity: 0.6;
  background-color: #f3f3f3;
}
.v-btn.v-btn--disabled.v-btn--has-bg {
  background-color: var(--v-webgrey-base) !important;
}

.psl-base-radio--button {
  :deep(.v-input--radio-group__input) {
    gap: 12px;
  }
}

@media screen and (max-width: $grid-breakpoints-lg) {
  // pslStyle 'image' styles
  .radio-image__wrapper {
    overflow-y: hidden;
    overflow-x: auto;

    :deep(.v-input--radio-group__input) {
      flex-wrap: nowrap;
    }
  }
}
</style>
